/**
 * Gift With Subscription
 * Automatically adds Holiday Gift Sampler to cart when a 6 or 12 month
 * subscription is added. Removes it if the qualifying subscription is removed.
 */

(function() {
  // Configuration
  const CONFIG = {
    samplerVariantId: '53391468593462',
    samplerHandle: 'holiday-gift-sampler',
    qualifyingPlanPatterns: ['6 month', '12 month', '6-month', '12-month'],
    notificationDuration: 5000,
    debug: true // Set to true to enable console logging
  };

  function log(...args) {
    if (CONFIG.debug) console.log('[Gift With Subscription]', ...args);
  }

  // Track if we manually removed the sampler (to prevent re-adding)
  let userRemovedSampler = false;

  // Prevent multiple simultaneous operations
  let isProcessing = false;

  /**
   * Check if a selling plan name qualifies for the free gift
   */
  function isQualifyingPlan(planName) {
    if (!planName) return false;
    const lowerName = planName.toLowerCase();
    return CONFIG.qualifyingPlanPatterns.some(pattern => lowerName.includes(pattern));
  }

  /**
   * Fetch cart from Shopify
   */
  async function checkCart() {
    const cart = await fetch('/cart.js').then(r => r.json());
    return { cart };
  }

  /**
   * Add the sampler to cart
   */
  async function addSampler() {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: CONFIG.samplerVariantId, quantity: 1 }]
        })
      });

      if (response.ok) {
        // Reset the user removed flag since we're adding it
        userRemovedSampler = false;

        // Trigger cart refresh
        document.dispatchEvent(new CustomEvent('theme:update:cart'));

        // Show notification
        showNotification();

        return true;
      }
    } catch (error) {
      console.error('Failed to add Holiday Gift Sampler:', error);
    }
    return false;
  }

  /**
   * Remove the sampler from cart
   */
  async function removeSampler(itemKey) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });

      if (response.ok) {
        // Trigger cart refresh
        document.dispatchEvent(new CustomEvent('theme:update:cart'));
        return true;
      }
    } catch (error) {
      console.error('Failed to remove Holiday Gift Sampler:', error);
    }
    return false;
  }

  /**
   * Show notification toast
   */
  function showNotification() {
    // Remove any existing notification
    const existing = document.querySelector('.free-gift-notification');
    if (existing) existing.remove();

    const notification = document.createElement('div');
    notification.className = 'free-gift-notification';
    notification.innerHTML = `
      <div class="free-gift-notification__content">
        <strong>Holiday Gift Sampler Added!</strong>
        <p>Your 6+ month subscription includes a free coffee sampler.</p>
      </div>
      <button class="free-gift-notification__close" aria-label="Close">&times;</button>
    `;

    // Add close button handler
    notification.querySelector('.free-gift-notification__close').addEventListener('click', () => {
      notification.remove();
    });

    document.body.appendChild(notification);

    // Auto-remove after duration
    setTimeout(() => {
      if (notification.parentNode) {
        notification.classList.add('free-gift-notification--hiding');
        setTimeout(() => notification.remove(), 300);
      }
    }, CONFIG.notificationDuration);
  }

  /**
   * Process cart data and determine if we need to add/remove sampler
   */
  function processCart(cart) {
    log('Processing cart with', cart.items?.length || 0, 'items');

    const hasQualifyingPlan = (cart.items || []).some(item => {
      const planName = item.selling_plan_allocation?.selling_plan?.name || '';
      log('Item selling plan:', planName, '| Qualifies:', isQualifyingPlan(planName));
      return isQualifyingPlan(planName);
    });

    // Check for sampler using multiple methods to be thorough
    const samplerItem = (cart.items || []).find(item => {
      const variantIdMatch = String(item.variant_id) === CONFIG.samplerVariantId;
      const handleMatch = item.handle === CONFIG.samplerHandle;
      const productHandleMatch = item.product_handle === CONFIG.samplerHandle;

      if (variantIdMatch || handleMatch || productHandleMatch) {
        log('Found sampler in cart:', item.variant_id, item.handle, item.product_handle);
        return true;
      }
      return false;
    });

    log('Has qualifying plan:', hasQualifyingPlan, '| Sampler in cart:', !!samplerItem);

    return { hasQualifyingPlan, samplerItem };
  }

  /**
   * Main handler for cart changes (fetches cart first)
   */
  async function handleCartChange() {
    const { cart } = await checkCart();
    await handleCartChangeWithCart(cart);
  }

  /**
   * Handle cart change with provided cart data
   */
  async function handleCartChangeWithCart(cart) {
    // Prevent multiple simultaneous operations
    if (isProcessing) {
      log('Already processing, skipping');
      return;
    }

    isProcessing = true;

    try {
      const { hasQualifyingPlan, samplerItem } = processCart(cart);

      if (hasQualifyingPlan && !samplerItem && !userRemovedSampler) {
        log('Adding sampler to cart');
        await addSampler();
      } else if (!hasQualifyingPlan && samplerItem) {
        log('Removing sampler from cart');
        await removeSampler(samplerItem.key);
      }
    } finally {
      // Small delay before allowing next operation
      setTimeout(() => {
        isProcessing = false;
      }, 1000);
    }
  }

  /**
   * Watch for cart item removals to detect if user manually removed sampler
   */
  function watchForManualRemoval() {
    // Monitor clicks on remove buttons
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-item], .cart-item__remove, [href*="/cart/change"]');
      if (removeBtn) {
        // Check if this is removing the sampler
        const cartItem = removeBtn.closest('[data-variant-id], .cart-item');
        if (cartItem) {
          const variantId = cartItem.dataset?.variantId ||
                           cartItem.querySelector('[data-variant-id]')?.dataset?.variantId;
          if (variantId === CONFIG.samplerVariantId) {
            userRemovedSampler = true;
          }
        }
      }
    });
  }

  // Initialize
  function init() {
    log('Script initialized');

    // Listen for product additions
    document.addEventListener('theme:product:add', async (e) => {
      log('theme:product:add event fired');

      // Try to get cart from the event's cartPromise first (more reliable in preview mode)
      if (e.detail?.cartPromise) {
        try {
          const cart = await e.detail.cartPromise;
          log('Cart from event promise');
          await handleCartChangeWithCart(cart);
          return;
        } catch (err) {
          log('cartPromise failed, falling back to fetch');
        }
      }

      // Fallback to fetching cart
      handleCartChange();
    });

    // Listen for cart updates
    document.addEventListener('theme:cart:change', handleCartChange);
    document.addEventListener('theme:update:cart', () => {
      // Small delay to let cart update complete
      setTimeout(handleCartChange, 500);
    });

    // Watch for manual removal of sampler
    watchForManualRemoval();

    // Check cart on page load (in case they already have a qualifying subscription)
    handleCartChange();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
